import { useMemo, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { Volume2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { isReadOnlyUser } from "@/lib/roles";
import { rfiUnread, useNotifications } from "@/lib/notifications";
import { replyLines, speakLines, speechOutputSupported, stopSpeaking, useSpeakingTag } from "@/lib/speech";
import { BuiltBuddyLauncherButton, useLauncherSlot } from "@/components/builtbuddy/BuiltBuddyLauncher";
import { BuiltBuddyGreeting, useGreeting } from "@/components/builtbuddy/BuiltBuddyGreeting";
import { BuiltBuddyAssistantPanel } from "@/components/builtbuddy/BuiltBuddyAssistantPanel";
import { useChatWidget } from "@/lib/chatWidget";

export const LAST_JOB_KEY = "bb_last_job";
const SPEECH_TAG = "bb-global-replies";

/** Remembered by BuiltBuddyDock whenever the panel opens on a job. */
export const readLastJob = () => {
  try {
    const v = JSON.parse(localStorage.getItem(LAST_JOB_KEY) || "null");
    return v && v.id ? v : null;
  } catch {
    return null;
  }
};

/**
 * BuiltBuddy everywhere — the same navy chat bubble in a bottom corner of every page.
 * Tapping it opens the chat-first assistant panel (greeting + starter prompts + composer — never a job list).
 * Job context is optional: BuiltBuddy asks which job when a question needs one; "Switch job" in the panel loads a job's
 * drawings & data at /jobs/{id}?bb=1. Hold the bubble (500 ms) to jump to the last job with the mic listening; drag it
 * to park it in either corner. On a Job page the page's own BuiltBuddyDock renders the launcher + panel instead.
 */
export const BuiltBuddyGlobalLauncher = ({ offset = null }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const onJobPage = !!useMatch("/jobs/:jobId/*");
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const last = useMemo(() => (open ? readLastJob() : null), [open]);
  const notes = useNotifications();
  const unread = rfiUnread(notes.items);
  const speaking = useSpeakingTag(SPEECH_TAG);
  const widget = useChatWidget();
  const greeting = useGreeting({ open, enabled: widget.greeting_enabled });

  const go = (job, voice = false) => {
    setOpen(false);
    navigate(`/jobs/${job.id}?bb=1${voice ? "&bb_voice=1" : ""}`);
  };
  /** Read the fresh RFI replies aloud (newest first, max 3). Stays on this page; a tap stops it. */
  const hearReplies = () => {
    if (!unread.length) return false;
    if (!speechOutputSupported()) {
      toast.error("Read-aloud isn't supported in this browser — open the bell to read the replies");
      return false;
    }
    setOpen(false);
    speakLines(replyLines(unread), { tag: SPEECH_TAG }).then((status) => {
      if (status === "error") toast.message("Couldn't read the replies aloud here — open the bell to read them");
    });
    return true;
  };
  const slot = useLauncherSlot({
    onTap: () => {
      if (speaking) {
        stopSpeaking(); // tap while reading = stop
        return;
      }
      setOpen((v) => !v);
    },
    onHold: () => {
      if (speaking) {
        stopSpeaking();
        return;
      }
      if (hearReplies()) return; // fresh answers first — then the usual hold-to-talk on a later hold
      const lj = readLastJob();
      if (lj) go(lj, true);
      else setOpen(true);
    },
    offset,
  });

  if (!user || user.role === "contractor" || user.role === "client" || isReadOnlyUser(user) || onJobPage) return null; // Phase 191 — chat creates data

  return (
    <div {...slot.slotProps} data-testid="builtbuddy-global-launcher">
      {greeting.visible ? (
        <BuiltBuddyGreeting text={widget.greeting_text} avatar={widget.avatar_style} corner={slot.corner} onOpen={() => setOpen(true)} onDismiss={greeting.dismiss} />
      ) : null}
      <BuiltBuddyLauncherButton
        ref={btnRef}
        {...slot.buttonProps}
        state={speaking ? "speaking" : "idle"}
        badge={unread.length}
        pulse={unread.length > 0 && !speaking}
        title={speaking ? "Reading the replies · tap to stop" : `${unread.length ? `${unread.length} RFI repl${unread.length === 1 ? "y" : "ies"} landed · hold to hear them` : "Tap to open · hold to talk"}`}
      />
      {unread.length && open ? (
        <Button
          size="sm"
          className="fixed bottom-24 right-6 z-[46] h-8 gap-1 bg-brand-steel px-2.5 text-[11px] text-white shadow-md hover:bg-brand-steel-deep"
          onClick={hearReplies}
          title="Read the new replies aloud (or hold the bubble)"
          data-testid="builtbuddy-hear-replies"
        >
          <Volume2 className="h-3.5 w-3.5" /> Hear {unread.length} RFI repl{unread.length === 1 ? "y" : "ies"}
        </Button>
      ) : null}
      <BuiltBuddyAssistantPanel open={open} onOpenChange={setOpen} lastJob={last} onNavigateJob={go} user={user} />
    </div>
  );
};
